<script>
  import { onDestroy, onMount } from 'svelte';
  import { link, querystring } from 'svelte-spa-router';
  import { authApi } from '../stores/api.js';
  import { authStore, logout } from '../stores/auth.js';
  import { INWD_CONSOLE_URL } from '../lib/inwd.js';
  import { navigateTo } from '../lib/navigation.js';
  import { _ } from '../lib/i18n.js';
  import AuthCard from '../components/AuthCard.svelte';
  import AuthNotice from '../components/AuthNotice.svelte';

  /** Whether the INWD console sent the browser here. */
  $: fromConsole = new URLSearchParams($querystring || '').get('from') === 'console';

  let all = false;
  let isLoading = false;
  /** `done` | `pending` once signed out. */
  let outcome = '';
  let error = '';
  let referrerMeta;

  onMount(() => {
    // Nothing on this page, nor the console it returns to, needs to know
    // where the browser came from.
    referrerMeta = document.createElement('meta');
    referrerMeta.name = 'referrer';
    referrerMeta.content = 'no-referrer';
    document.head.appendChild(referrerMeta);
  });

  onDestroy(() => {
    referrerMeta?.remove();
  });

  async function signOut() {
    if (isLoading) return;
    isLoading = true;
    error = '';
    try {
      const result = await authApi.terminateSessions({ all });
      outcome = Array.isArray(result?.warnings) && result.warnings.length > 0 ? 'pending' : 'done';
      // The server already revoked the session; drop this page's copy of it.
      await logout();
      if (outcome === 'done' && fromConsole) navigateTo(INWD_CONSOLE_URL);
    } catch {
      error = $_('auth.signOut.error');
    } finally {
      isLoading = false;
    }
  }
</script>

<AuthCard title={$_('auth.signOut.title')}>
  {#if outcome === 'done'}
    <AuthNotice tone="success" testid="logout-done">
      {fromConsole ? $_('auth.signOut.redirecting') : $_('auth.signOut.done')}
    </AuthNotice>
  {:else if outcome === 'pending'}
    <AuthNotice tone="info" testid="logout-pending">{$_('auth.signOut.pending')}</AuthNotice>
  {:else if !$authStore.isAuthenticated}
    <AuthNotice tone="info" testid="logout-already">{$_('auth.signOut.already')}</AuthNotice>
  {:else}
    <p class="text-sm" style="color: var(--color-text-muted);">{$_('auth.signOut.intro')}</p>
    {#if error}
      <AuthNotice tone="error" testid="logout-error">{error}</AuthNotice>
    {/if}
    <label class="flex items-center space-x-2 text-sm" style="color: var(--color-text-muted);">
      <input type="checkbox" bind:checked={all} disabled={isLoading} data-testid="logout-all" />
      <span>{$_('auth.signOut.all')}</span>
    </label>
    <button
      type="button"
      class="w-full btn btn-primary h-12 text-base"
      on:click={signOut}
      disabled={isLoading}
      data-testid="logout-submit"
    >
      {isLoading ? `${$_('common.loading')}...` : $_('auth.signOut.submit')}
    </button>
  {/if}
  {#if fromConsole && outcome !== 'done'}
    <p class="text-center text-sm">
      <a href={INWD_CONSOLE_URL} rel="noreferrer" class="font-bold text-primary-500 hover:underline" data-testid="logout-console-link">
        {$_('auth.signOut.backToConsole')}
      </a>
    </p>
  {:else if !fromConsole}
    <p class="text-center text-sm">
      <a href="/login" use:link class="font-bold text-primary-500 hover:underline">{$_('auth.backToLogin')}</a>
    </p>
  {/if}
</AuthCard>
